import { usePreInstallGcr } from "@/api/useIntegrations"
import { Flex } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { INTEGRATIONS_WRITE_PERMISSION } from "@/pages/IntegrationsPage/IntegrationsPage.constants"
import { GcrServiceAccount } from "@/pages/IntegrationsPage/components/molecules/GcrServiceAccount"
import { gcrGrantTarget } from "@/pages/IntegrationsPage/helpers/gcr"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"
import { GcrConfigurationItem } from "./GcrConfigurationItem"

import type { GcrConfiguration } from "@/api/useIntegrations.types"
import type { GcrConfigurationTabProps } from "."

export const GcrConfigurationTab = (props: GcrConfigurationTabProps) => {
  const { t } = useTranslation()
  const [data] = useIntegration(props)
  const { hasPermission } = useUser()
  const canWrite = !!hasPermission(INTEGRATIONS_WRITE_PERMISSION)
  const configuration = data?.configuration as GcrConfiguration | undefined
  const registries = configuration?.installations || []
  // Viewers can't create the service account, and the page shouldn't fail for them.
  const { response, isError, isFetching, refetch } = usePreInstallGcr({
    enabled: canWrite && registries.length > 0
  })
  const email = response?.data?.serviceAccountEmail

  if (!registries.length) return <>{t("integrations.gcr.noData")}</>

  return (
    <Flex vertical gap={8}>
      {canWrite && (
        <GcrServiceAccount
          email={email}
          target={gcrGrantTarget({
            locations: registries.flatMap((r) =>
              r.location ? [r.location] : []
            ),
            repositories: registries.flatMap((r) => r.repositories)
          })}
          failed={isError && !isFetching}
          onRetry={() => refetch()}
        />
      )}
      {registries.map((registry) => (
        <GcrConfigurationItem
          key={registry.registryUrl}
          registry={registry}
          tenantServiceAccount={
            email || registry.serviceAccountEmail || undefined
          }
          {...props}
        />
      ))}
    </Flex>
  )
}
